import type { Viewer } from '../viewer.ts';
import { HALF_X } from './station.ts';
import type { Train } from './train.ts';

/**
 * Trains arrive, sit with their doors open, and leave, forever.
 *
 * The point of interest is that this keeps the viewer's best property. Two of
 * the six phases are completely static, so the render loop is switched off for
 * them and the app draws zero frames - a duty cycle around 40% rather than
 * animating flat out. Door travel is split into its own phases for exactly that
 * reason: it means the long middle of the dwell is genuinely motionless.
 *
 * Everything is driven by elapsed seconds, never frame count, so it runs at the
 * same speed whatever frame rate a device manages.
 *
 * Each phase's duration is rerolled within a jitter band every time it is
 * entered (see JITTER below), independently per loop. Two loops built with
 * identical tables and no randomness would stay at a perfectly fixed offset
 * forever - which sounds harmless until something (the dev panel's "jump to
 * phase", say) resets both to elapsed=0 at the same instant, and then they run
 * in exact lockstep for good, since nothing ever perturbs them apart again.
 * With independently-rolled durations, two trains forced into sync still drift
 * apart over the next few cycles on their own, the way real headways do.
 */

type PhaseName = 'approach' | 'doorsOpen' | 'hold' | 'doorsClose' | 'depart' | 'gap';

interface Phase {
  /** Seconds. Feel, not spec - tune by watching it. */
  duration: number;
  /**
   * +/- fraction of `duration` to roll on each entry. The static phases carry
   * the most jitter, since they are what set the interval *between* trains
   * (dwell time, headway) - real timetables vary there far more than they vary
   * how fast a train physically accelerates.
   */
  jitter: number;
  /** True when nothing moves, so the renderer can be switched off. */
  static: boolean;
  next: PhaseName;
  /**
   * Pose the train for a progress value in 0..1. `reach` is the signed distance
   * the train travels to enter or leave, so a train running the other way is the
   * same maths with the sign flipped.
   */
  apply(t: number, train: Train, reach: number): void;
}

/** duration * (1 + uniform(-jitter, +jitter)). */
function rollDuration(phase: Phase): number {
  return phase.duration * (1 + (Math.random() * 2 - 1) * phase.jitter);
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;
const easeIn = (t: number) => t ** 3;

const PHASES: Record<PhaseName, Phase> = {
  approach: {
    duration: 5,
    jitter: 0.08,
    static: false,
    next: 'doorsOpen',
    apply(t, train, reach) {
      train.group.position.x = -reach * (1 - easeOut(t));
      train.setDoors(0);
    },
  },
  doorsOpen: {
    duration: 1.2,
    jitter: 0.1,
    static: false,
    next: 'hold',
    apply(t, train) {
      train.group.position.x = 0;
      train.setDoors(t);
    },
  },
  hold: {
    duration: 6,
    // Boarding time is the most naturally variable part of a real stop.
    jitter: 0.3,
    static: true,
    next: 'doorsClose',
    apply(_t, train) {
      train.group.position.x = 0;
      train.setDoors(1);
    },
  },
  doorsClose: {
    duration: 1.2,
    jitter: 0.1,
    static: false,
    next: 'depart',
    apply(t, train) {
      train.group.position.x = 0;
      train.setDoors(1 - t);
    },
  },
  depart: {
    duration: 5,
    jitter: 0.08,
    static: false,
    next: 'gap',
    apply(t, train, reach) {
      train.group.position.x = easeIn(t) * reach;
      train.setDoors(0);
    },
  },
  gap: {
    duration: 6,
    // Headway - the gap between one train leaving and the next arriving - is
    // the other big source of real-world variability, and jittering it here is
    // what actually keeps two trains from staying at a fixed offset forever.
    // Wider than hold's: this is the one phase whose only job is to be the
    // interval between trains, so it can carry more swing than a passenger
    // dwell can without looking wrong.
    jitter: 0.6,
    static: true,
    next: 'approach',
    apply(_t, train, reach) {
      train.group.position.x = reach;
      train.setDoors(0);
    },
  },
};

export interface TrainLoop {
  /** Advance by dt seconds. Call from the viewer's per-frame hook. */
  update(dt: number): void;
  /**
   * Multiply the rate the whole cycle runs at. Applies to the timers behind the
   * static phases as well as the moving ones - scaling only the moving phases
   * would speed the train up but leave the dwell the same length, which is not
   * what anyone means by "faster".
   */
  setTimeScale(scale: number): void;
  /** Jump straight to a phase. Development aid. */
  goTo(phase: PhaseName): void;
  stop(): void;
}

export type { PhaseName };

export interface TrainLoopOptions {
  /** +1 runs west to east (enters from -X); -1 is the other way. */
  direction?: 1 | -1;
  /**
   * Extra seconds on the first gap. Two trains on the same timings would
   * otherwise arrive and leave in perfect lockstep, which reads as a mechanism
   * rather than as a station.
   */
  startDelay?: number;
  /**
   * Distinguishes this loop's claim on the render loop from any other's. Two
   * loops sharing one reason would let the first to finish stop the second.
   */
  reason?: string;
}

export function createTrainLoop(
  train: Train,
  viewer: Viewer,
  options: TrainLoopOptions = {},
): TrainLoop {
  const { direction = 1, startDelay = 0, reason = 'train' } = options;

  // Far enough out that the train is completely behind the tunnel mouth. Signed,
  // so one train enters from the left and the other from the right.
  const reach = direction * (HALF_X + train.length / 2 + 2);

  let current: PhaseName = 'gap';
  let elapsed = 0;
  // The rolled duration this entry of the phase is actually running for. Moving
  // phases need this in update() too - using the static PHASES table there
  // would silently discard the jitter the instant it was rolled.
  let currentDuration = 0;
  let firstGap = true;
  let timer: number | undefined;
  let stopped = false;
  let timeScale = 1;

  function enter(name: PhaseName) {
    current = name;
    elapsed = 0;
    const phase = PHASES[name];
    phase.apply(0, train, reach);

    // Rolled independently per loop, per entry: this is what lets two loops
    // that get forced into sync (e.g. by the dev panel's "jump to phase", which
    // moves every loop at once) drift back apart on their own over the next few
    // cycles, rather than staying at whatever offset - including zero - they
    // were left at.
    let duration = rollDuration(phase);
    if (name === 'gap' && firstGap) {
      duration += startDelay;
      firstGap = false;
    }
    currentDuration = duration;

    globalThis.clearTimeout(timer);
    timer = undefined;

    if (phase.static) {
      // Draw the phase once, then go quiet and set an alarm rather than burning
      // frames watching a stationary train.
      viewer.setAnimating(false, reason);
      viewer.invalidate();
      timer = globalThis.setTimeout(() => {
        timer = undefined;
        if (!stopped) enter(phase.next);
      }, (duration * 1000) / Math.max(timeScale, 0.01));
    } else {
      viewer.setAnimating(true, reason);
    }
  }

  function update(dt: number) {
    if (stopped) return;
    const phase = PHASES[current];
    // Static phases advance on their timer. A stray frame from a camera drag
    // must not push them along.
    if (phase.static) return;

    elapsed += dt * timeScale;
    const t = Math.min(elapsed / currentDuration, 1);
    phase.apply(t, train, reach);
    if (t >= 1) enter(phase.next);
  }

  enter('gap');

  return {
    update,
    setTimeScale(scale) {
      timeScale = Math.max(scale, 0.01);
      // Re-enter so a running static phase picks up the new rate rather than
      // sitting out the rest of a timer set at the old one.
      if (PHASES[current].static) enter(current);
    },
    goTo(phase) {
      enter(phase);
    },
    stop() {
      stopped = true;
      globalThis.clearTimeout(timer);
      viewer.setAnimating(false, reason);
    },
  };
}
